import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { notify } from '../../../services/notification';
import Header from "../../../component/loaderComponent/Header/Header";
import { getDbtConfigAction, updateDbtConfigAction } from "../../../actions/dbtConfigurationAction";
import { HTTP_STATUS } from "../../../constants/constant";
import './dbtConfiguration.scss';

export default function DbtConfiguration() {
  const dispatch = useDispatch();

  const [config, setConfig] = useState({
    is_active: false,
    incentive_percentage: ''
  });

  const [originalConfig, setOriginalConfig] = useState({
    is_active: false,
    incentive_percentage: ''
  });

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Fetch configuration on mount
  useEffect(() => {
    fetchConfiguration();
  }, []);

  const fetchConfiguration = async () => {
    setLoading(true);
    try {
      const response = await dispatch(getDbtConfigAction());
      if (response?.status === HTTP_STATUS.OK) {
        const data = {
          is_active: response?.data?.data?.is_active || false,
          incentive_percentage: response?.data?.data?.incentive_percentage || ''
        };
        setConfig(data);
        setOriginalConfig(data);
      }
    } catch (error) {
      notify.error("Failed to load DBT configuration");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleChange = (e) => {
    const isActive = e.target.checked;
    setConfig({
      ...config,
      is_active: isActive,
      incentive_percentage: isActive ? config.incentive_percentage : ''
    });
    setErrors({});
  };

  // const handlePercentageChange = (e) => {
  //   const value = e.target.value;
  //   // Allow only numbers and decimal point
  //   if (value === '' || /^\d*\.?\d*$/.test(value)) {
  //     setConfig({
  //       ...config,
  //       discount_percentage: value
  //     });
  //     setErrors({});
  //   }
  // };

  const handlePercentageChange = (e) => {
    const value = e.target.value;
    // Allow empty input
    if (value === '') {
      setConfig({ ...config, incentive_percentage: '' });
      setErrors({});
      return;
    }
    // Allow only numbers and decimal point
    if (/^\d*\.?\d*$/.test(value)) {
      const numberValue = Number(value);
      // Enforce 0.1–100 range
      if (numberValue >= 0 && numberValue <= 100) {
        setConfig({ ...config, incentive_percentage: value });
        setErrors({});
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (config.is_active) {
      if (!config.incentive_percentage || config.incentive_percentage === '') {
        newErrors.incentive_percentage = "DBT % Multiplier for Non-primary APS percentage is required when DBT is enabled";
      } else {
        const percentage = parseFloat(config.incentive_percentage);
        if (isNaN(percentage)) {
          newErrors.incentive_percentage = "Invalid percentage value";
        } else if (percentage < 0.1) {
          newErrors.incentive_percentage = "Percentage must be at least 0.1";
        } else if (percentage > 100) {
          newErrors.incentive_percentage = "Percentage cannot exceed 100";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const hasChanges = () => {
    return config.is_active !== originalConfig.is_active ||
      config.incentive_percentage !== originalConfig.incentive_percentage;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        is_active: config.is_active,
        ...(config.is_active && {
          incentive_percentage: parseFloat(config.incentive_percentage),
        }),
      };
      
      const response = await dispatch(updateDbtConfigAction(payload));

      if (response?.status === HTTP_STATUS.CREATED) {
        notify.success(response?.data?.message || "DBT configuration updated successfully");

        // Refresh configuration from server
        await fetchConfiguration();
      }
    } catch (error) {
      notify.error(error?.response?.data?.message || "Failed to update DBT configuration");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="dbt-config-container">
        <div className="dbt-config-container-right">
          <Header title="Settings" />
          <div className="dbt-config-loading">Loading configuration...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="dbt-config-container">
      <div className="dbt-config-container-right">
        <Header title="DBT Configuration Settings" />

        <div className="dbt-config-heading">
          <span className="dbt-config-heading-text">Non Primary APS Configuration</span>
          <span className="dbt-config-subtitle">
            Configure multiplier percentage for DBT non-primary APS Configuration
          </span>
        </div>

        <div className="dbt-config-content">
          {/* Toggle Section */}
          <div className="dbt-config-row">
            <div className="dbt-config-label-section">
              <label className="dbt-config-label">Enable DBT Eligibility for Non-Primary APS </label>
              <span className="dbt-config-help-text">
                Toggle to enable/disable DBT for Non Primary APS Configuration
              </span>
            </div>
            <div className="dbt-config-toggle-input-section">
              <label className="switch">
                <input
                  type="checkbox"
                  checked={config.is_active}
                  onChange={handleToggleChange}
                  disabled={submitting}
                />
                <span className="slider round"></span>
              </label>
              <span className="toggle-status">
                {config.is_active ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>

          {/* Conditional Percentage Input */}
          {config.is_active && (
            <div className="dbt-config-row">
              <div className="dbt-config-label-section">
                <label className="dbt-config-label">
                  DBT % Multiplier for Non-primary APS <span className="required">*</span>
                </label>
                <span className="dbt-config-help-text">
                  Enter the multiplier percentage (0.1-100)
                </span>
              </div>
              <div className="dbt-config-input-section">
                <div className="percentage-input-wrapper">
                  <input
                    type="text"
                    className={`dbt-config-input ${errors.incentive_percentage ? 'error' : ''}`}
                    placeholder="Enter percentage"
                    value={config.incentive_percentage}
                    onChange={handlePercentageChange}
                    disabled={submitting}
                    maxLength={6}
                  />
                  <span className="percentage-symbol">%</span>
                </div>
                {errors.incentive_percentage && (
                  <span className="error-message">{errors.incentive_percentage}</span>
                )}
              </div>
            </div>
          )}

          {/* Submit Button - Show only if changes made */}
          {hasChanges() && (
            <div className="dbt-config-actions">
              <button
                className="dbt-config-submit-button"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? 'Saving...' : 'Save Configuration'}
              </button>
              <button
                className="dbt-config-cancel-button"
                onClick={() => {
                  setConfig(originalConfig);
                  setErrors({});
                }}
                disabled={submitting}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
